import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signIn } from '../../services/authService';
import useAuthStore from '../../store/authStore';
import { Mail, Lock, AlertCircle } from 'lucide-react';

// Reusable Input component
const Input = ({
  label,
  id,
  name,
  type = 'text',
  value,
  onChange,
  required = false,
  placeholder = '',
  icon: Icon,
  className = '',
  ...props
}: {
  label: string;
  id: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
  icon?: React.ElementType;
  className?: string;
  [key: string]: any;
}) => {
  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 mr-1">*</span>}
      </label>
      <div className="relative rounded-md shadow-sm">
        {Icon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
        )}
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          className={`block w-full pr-${Icon ? '10' : '3'} pl-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${className}`}
          dir={type === 'email' ? 'ltr' : 'rtl'}
          {...props}
        />
      </div>
    </div>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { setUser, setLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('الرجاء ملء جميع الحقول');
      return;
    }

    try {
      setIsLoading(true);
      setLoading(true);
      const userCredential = await signIn(email, password);
      if (userCredential?.user) {
        setUser(userCredential.user);
        // You might want to fetch additional user data here
        // const userData = await getUserData(userCredential.user.uid);
        // setUserData(userData);
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('البريد الإلكتروني أو كلمة المرور غير صحيحة');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <h1 className="text-center text-3xl font-DancingScript font-bold text-blue-600 mb-8">LexPay</h1>
      </div>

      <div className="max-w-md mx-auto bg-white p-8 rounded-2xl shadow-sm w-full">
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
          تسجيل الدخول
        </h2>

        {error && (
          <div className="mb-6 p-4 text-sm text-red-700 bg-red-100 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 ml-2 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <Input
              label="البريد الإلكتروني"
              id="email"
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@example.com"
              icon={Mail}
            />

            <Input
              label="كلمة المرور"
              id="password"
              name="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              icon={Lock}
            />
          </div>

          <div className="flex items-center justify-between">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              هل نسيت كلمة المرور؟
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                padding: '0.5rem 1rem',
                border: '1px solid transparent',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#ffffff',
                backgroundColor: 'oklch(0.55 0.24 262.91)',
                transition: 'background-color 0.2s, opacity 0.2s',
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                outline: 'none'
              }}
              onMouseOver={e => !isLoading && (e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)')}
              onMouseOut={e => !isLoading && (e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)')}
              onFocus={e => !isLoading && (e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)')}
              onBlur={e => !isLoading && (e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)')}
            >
              {isLoading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
            </button>
          </div>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                ليس لديك حساب؟
              </span>
            </div>
          </div>

          <div className="mt-6">
            <Link
              to="/signup"
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151',
                backgroundColor: '#ffffff',
                transition: 'background-color 0.2s',
                textDecoration: 'none',
                textAlign: 'center',
                cursor: 'pointer'
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = '#ffffff'}
              onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)'}
              onBlur={e => e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)'}
            >
              إنشاء حساب جديد
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
